// ============================================================================
// TRIAGE QUEUE HEALTH VIEW — Triage Nurse SLA Dashboard
//
// Role access: triage_nurse
//
// Data source: LIVE workflow/state reads (via listOpsActiveItems).
//   Stuck detection uses tenant Impact Settings SLA thresholds with
//   business-hours-aware elapsed time for triage sub-clocks.
//
// Firestore reads:
//   - tenants/{tenantId}/referrals (active items via listOpsActiveItems)
//   - tenants/{tenantId}/settings/impact (SLA + business hours config)
//
// Layout:
//   TOP ROW    = STUCK CARDS — referrals exceeding triage SLA by sub-category
//   BOTTOM ROW = VOLUME CARDS — raw counts by triage sub-category (no SLA)
//
// SLA source of truth: sla.triage from tenant Impact Settings
//   - urgentBusinessHours    → Stuck Urgent card
//   - routineBusinessHours   → Stuck Routine card
//   - clinicalHoldBusinessHours → Stuck on Hold card
//
// Zero-PHI invariant: this view never reads from /phi/**.
// ============================================================================

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  listOpsActiveItems,
  getImpactSettings,
  DEFAULT_IMPACT_SETTINGS,
  WORKFLOW_STATUS,
  businessHoursElapsed,
  queueSlaStatus,
  classifyTriageOutcome,
} from '../../core/index.js';
import {
  Activity, RefreshCw, AlertTriangle, Flame,
  Clock, Zap, PauseCircle, Users,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function toMillis(ts) {
  if (!ts) return null;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  return null;
}


// ── Sub-components ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-slate-800/30 border border-slate-700/20 rounded-2xl p-5 animate-pulse">
      <div className="h-3 w-24 bg-slate-700/60 rounded mb-3" />
      <div className="h-10 w-16 bg-slate-700/60 rounded" />
    </div>
  );
}

const STATUS_STYLES = {
  ok:       { bg: 'bg-slate-800/30', border: 'border-slate-700/30', text: 'text-slate-500', pulse: '', badge: '' },
  // 'active' = breached count > 0 but below the warn threshold. Still shown visibly
  // (not greyed) and actionable, so a real SLA breach is never dismissed as "ok".
  active:   { bg: 'bg-slate-800/50', border: 'border-slate-600/40', text: 'text-slate-200', pulse: '', badge: 'bg-slate-600/40 text-slate-300' },
  warn:     { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', pulse: 'bg-amber-500 animate-pulse', badge: 'bg-amber-500/20 text-amber-300' },
  critical: { bg: 'bg-red-500/10',   border: 'border-red-500/20',   text: 'text-red-400',   pulse: 'bg-red-500 animate-pulse', badge: 'bg-red-500/20 text-red-300' },
};

function StuckCard({ label, slaLabel, stuckCount, queueCount, stuckPct, status, warnThreshold, critThreshold, icon: Icon, nuclearOverride, onClick }) {
  // A breach below warn keeps status 'ok' (queueSlaStatus has minWarn/minCrit gates).
  // Promote it to a visible 'active' tier so the count isn't greyed and stays actionable.
  const hasStuck = stuckCount > 0;
  const tier = status === 'critical' ? 'critical'
    : status === 'warn' ? 'warn'
    : (hasStuck ? 'active' : 'ok');
  const s = STATUS_STYLES[tier] || STATUS_STYLES.ok;
  const isAlert = tier === 'critical' || tier === 'warn';
  const isActive = tier === 'active';
  const visible = isAlert || isActive;
  const clickable = visible && typeof onClick === 'function';

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      className={`relative overflow-hidden rounded-2xl border p-5 transition-all w-full
        ${s.bg} ${s.border} ${s.text}
        ${clickable ? 'cursor-pointer hover:scale-[1.02] hover:brightness-110 active:scale-[0.98]' : ''}`}
    >
      {isAlert && s.pulse && (
        <div className={`absolute inset-0 ${s.pulse} opacity-10 rounded-2xl`} />
      )}
      <div className="relative flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-medium mb-1 ${visible ? 'opacity-80' : 'text-slate-500'}`}>
            {label}
          </p>
          {queueCount === 0 ? (
            <p className="text-sm text-slate-500 mt-2">No items in this category</p>
          ) : (
            <>
              <p className={`text-4xl font-black tracking-tight ${visible ? '' : 'text-slate-600'}`}>
                {stuckCount}
              </p>
              <p className={`text-xs mt-1 ${visible ? 'opacity-70' : 'text-slate-600'}`}>
                {stuckPct}% of queue stuck
              </p>
              <p className={`text-[10px] mt-0.5 ${visible ? 'opacity-50' : 'text-slate-600'}`}>
                Warn: {warnThreshold}% · Critical: {critThreshold}%
              </p>
            </>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            {nuclearOverride && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES.critical.badge}`}>
                <Flame className="w-3 h-3 inline mr-1" />Urgent SLA breach — forced Critical
              </span>
            )}
            {!nuclearOverride && tier === 'critical' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badge}`}>
                <Flame className="w-3 h-3 inline mr-1" />Critical — {stuckPct}% stuck
              </span>
            )}
            {tier === 'warn' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badge}`}>
                <AlertTriangle className="w-3 h-3 inline mr-1" />WARNING
              </span>
            )}
            {tier === 'active' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badge}`}>
                {stuckCount} stuck · below warn threshold
              </span>
            )}
            {tier === 'ok' && queueCount > 0 && (
              <span className="text-xs text-slate-500">Within threshold</span>
            )}
          </div>
          <p className={`text-[10px] mt-2 ${visible ? 'opacity-40' : 'text-slate-600'}`}>
            {slaLabel}
          </p>
        </div>
        <Icon className={`w-6 h-6 flex-shrink-0 ${visible ? 'opacity-40' : 'opacity-20'}`} />
      </div>
    </div>
  );
}

function VolumeCard({ label, count, icon: Icon, colorTw, subtitle }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl p-5 flex flex-col gap-1">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${colorTw}`} />
        <p className="text-xs font-medium text-slate-400">{label}</p>
      </div>
      <p className={`text-3xl font-bold ${colorTw}`}>
        {count}
      </p>
      {subtitle && (
        <p className="text-xs text-slate-500">{subtitle}</p>
      )}
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function TriageQueueHealthView({ onNavigate, activeFocusTarget }) {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId;

  const [items, setItems]       = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!tenantId) return;
    try {
      const [data, impactSettings] = await Promise.all([
        listOpsActiveItems(tenantId),
        getImpactSettings(tenantId),
      ]);
      setItems(data);
      setSettings(impactSettings || DEFAULT_IMPACT_SETTINGS);
      setError(null);
    } catch (err) {
      console.error('[TriageQueueHealthView] Failed to load:', err);
      setError('Failed to load triage data. Please retry.');
    }
  };

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    setItems(null);
    await load();
    setRefreshing(false);
  };

  const loading = items === null && !error;

  // ── Derive triage dashboard from live data + tenant SLA settings ────────

  const dashboard = useMemo(() => {
    if (!items || !settings) return null;
    const nowMs = Date.now();

    const sla = settings.sla || DEFAULT_IMPACT_SETTINGS.sla;
    const triageSla = sla.triage || DEFAULT_IMPACT_SETTINGS.sla.triage;
    const thresholds = sla.thresholds || DEFAULT_IMPACT_SETTINGS.sla.thresholds;
    const bh = settings.businessHours || DEFAULT_IMPACT_SETTINGS.businessHours;

    const warnPct = thresholds.warnPercent ?? 0.10;
    const critPct = thresholds.criticalPercent ?? 0.25;

    const urgentSlaHours  = triageSla.urgentBusinessHours ?? 4;
    const routineSlaHours = triageSla.routineBusinessHours ?? 48;
    const holdSlaHours    = triageSla.clinicalHoldBusinessHours ?? 72;

    // Filter to triage-relevant items: READY_TO_SCHEDULE that are NOT yet triaged
    const triageItems = items.filter(item => {
      const status = item.workflow?.status;
      if (status !== WORKFLOW_STATUS.READY_TO_SCHEDULE) return false;
      const w = item.workflow;
      return !(w?.triage?.triageCompletedAt || w?.triageCompletedAt);
    });

    let urgentStuck = 0, routineStuck = 0, holdStuck = 0;
    let urgentCount = 0, routineCount = 0, holdCount = 0;

    for (const item of triageItems) {
      const outcome = classifyTriageOutcome(item);
      const isOnHold = item.workflow?.onHold === true;

      if (isOnHold) {
        holdCount++;
        const holdStartMs = toMillis(item.workflow?.onHoldStartedAt);
        if (holdStartMs && businessHoursElapsed(holdStartMs, nowMs, bh) >= holdSlaHours) {
          holdStuck++;
        }
      } else if (outcome === 'URGENT') {
        urgentCount++;
        const enteredMs = toMillis(item.workflow?.statusEnteredAt) || toMillis(item.workflow?.statusUpdatedAt);
        if (enteredMs && businessHoursElapsed(enteredMs, nowMs, bh) >= urgentSlaHours) {
          urgentStuck++;
        }
      } else if (outcome === 'ROUTINE') {
        routineCount++;
        const enteredMs = toMillis(item.workflow?.statusEnteredAt) || toMillis(item.workflow?.statusUpdatedAt);
        if (enteredMs && businessHoursElapsed(enteredMs, nowMs, bh) >= routineSlaHours) {
          routineStuck++;
        }
      }
    }

    const totalStuck = urgentStuck + routineStuck + holdStuck;
    const totalCount = urgentCount + routineCount + holdCount;

    // Nuclear override: any urgent SLA breach forces Critical
    let overallStatus;
    let nuclearOverride = false;
    if (urgentStuck > 0) {
      overallStatus = 'critical';
      nuclearOverride = true;
    } else {
      overallStatus = queueSlaStatus(totalStuck, totalCount, warnPct, critPct);
    }

    function buildStuckCard(stuckCount, queueCount) {
      const stuckPct = queueCount > 0 ? Math.round((stuckCount / queueCount) * 100) : 0;
      return {
        stuckCount,
        queueCount,
        stuckPct,
        status: queueSlaStatus(stuckCount, queueCount, warnPct, critPct),
        warnThreshold: Math.round(warnPct * 100),
        critThreshold: Math.round(critPct * 100),
      };
    }

    return {
      urgent:  buildStuckCard(urgentStuck, urgentCount),
      routine: buildStuckCard(routineStuck, routineCount),
      hold:    buildStuckCard(holdStuck, holdCount),

      urgentCount,
      routineCount,
      holdCount,
      totalCount,
      totalStuck,
      overallStatus,
      nuclearOverride,

      urgentSlaHours,
      routineSlaHours,
      holdSlaHours,
    };
  }, [items, settings]);

  // ── Render ──────────────────────────────────────────────────────────────

  const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8" data-learning-target="queue-health-page">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={`flex items-center justify-between${activeFocusTarget === 'queue-health-header' ? ' rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="queue-health-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Queue Health</h1>
            <p className="text-xs text-slate-400">
              Triage — Live — {todayStr}
              {dashboard && (
                <span className="ml-2 text-slate-500">
                  · {dashboard.totalCount} pending triage
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                     text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={handleRefresh}
            className="text-xs underline underline-offset-2 hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── STUCK CARDS (SLA-driven) ──────────────────────────────────────── */}
      <section className={activeFocusTarget === 'queue-health-live-fires' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="queue-health-live-fires">
        <div className="flex items-center gap-2 mb-1">
          <Flame className="w-4 h-4 text-red-400" />
          <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider">
            Live Fires
          </h2>
          {dashboard && dashboard.totalStuck > 0 && (
            <span className="ml-1 w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          )}
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Referrals waiting longer than configured triage SLA (business hours)
        </p>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={activeFocusTarget === 'queue-health-stuck-urgent' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="queue-health-stuck-urgent">
              <StuckCard
                label="Stuck Urgent"
                slaLabel={`SLA: ${dashboard.urgentSlaHours}h business hours`}
                icon={Zap}
                nuclearOverride={dashboard.nuclearOverride}
                {...dashboard.urgent}
                status={dashboard.nuclearOverride ? 'critical' : dashboard.urgent.status}
                onClick={() => onNavigate('triage-queue', { tab: 'PENDING' })}
              />
            </div>
            <div className={activeFocusTarget === 'queue-health-stuck-routine' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="queue-health-stuck-routine">
              <StuckCard
                label="Stuck Routine"
                slaLabel={`SLA: ${dashboard.routineSlaHours}h business hours`}
                icon={Clock}
                {...dashboard.routine}
                onClick={() => onNavigate('triage-queue', { tab: 'PENDING' })}
              />
            </div>
            <div className={activeFocusTarget === 'queue-health-stuck-hold' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="queue-health-stuck-hold">
              <StuckCard
                label="Stuck on Hold"
                slaLabel={`SLA: ${dashboard.holdSlaHours}h business hours`}
                icon={PauseCircle}
                {...dashboard.hold}
                onClick={() => onNavigate('triage-queue', { tab: 'ON_HOLD' })}
              />
            </div>
          </div>
        )}

        {dashboard && dashboard.totalStuck === 0 && (
          <div className={`mt-2 px-4 py-3 bg-emerald-500/5 border border-emerald-500/15
                          rounded-xl text-emerald-400 text-sm flex items-center gap-2${activeFocusTarget === 'queue-health-within-sla' ? ' transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="queue-health-within-sla">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            All triage items within SLA — no stuck referrals.
          </div>
        )}
      </section>

      {/* ── VOLUME CARDS (raw counts, no SLA) ─────────────────────────────── */}
      <section className={activeFocusTarget === 'queue-health-total-volume' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''} data-learning-target="queue-health-total-volume">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-blue-400" />
          <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">
            Engine — Total Volume
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-3 gap-4">
            <VolumeCard
              label="Urgent Queue"
              count={dashboard.urgentCount}
              icon={Zap}
              colorTw="text-red-400"
              subtitle="Outcome: Urgent"
            />
            <VolumeCard
              label="Routine Queue"
              count={dashboard.routineCount}
              icon={Clock}
              colorTw="text-blue-400"
              subtitle="Outcome: Routine"
            />
            <VolumeCard
              label="Hold Queue"
              count={dashboard.holdCount}
              icon={PauseCircle}
              colorTw="text-amber-400"
              subtitle="Clinical hold active"
            />
          </div>
        )}
      </section>

      {/* ── Footer note ────────────────────────────────────────────────────── */}
      <p className="text-xs text-slate-600 text-center">
        Live data from workflow states · Triage SLA from tenant Impact Settings · Business-hours-aware elapsed time
      </p>
    </div>
  );
}
